import { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface AboutUsProps {
  isLoggedIn: boolean;
}

const AboutUs: FunctionComponent<AboutUsProps> = ({ isLoggedIn }) => {
  return (
    <div className="bg-background min-h-screen">
      <Helmet>
        <title>About EatMeat — #1 Carnivore & Ketovore Diet App</title>
        <meta name="description" content="Learn about EatMeat — the app built for carnivore and ketovore lifestyles. Meet our team, our mission, and why 50,000+ users trust us with their health journey." />
        <link rel="canonical" href="https://www.eatmeatapp.com/about" />
      </Helmet>
      {/* Hero Section */}
      <section className="hero-section py-16 md:py-24 animate-fade-in">
        <div className="container mx-auto px-6 text-center text-background">
          <h1 className="hero-heading mb-4">About EatMeat</h1>
          <p className="hero-subtitle max-w-2xl mx-auto">
            EatMeat is your ultimate destination for celebrating the joy of meat-centric meals, connecting food lovers, and sharing delicious recipes.
          </p>
          <a
            href={isLoggedIn ? "/profile" : "/signup"}
            className="hero-button inline-block bg-primary text-background px-6 py-3 rounded-lg hover:bg-accent focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {isLoggedIn ? "Explore Your Profile" : "Join the Community"}
          </a>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-primary text-center mb-12 animate-fade-in">
            Our Mission
          </h2>
          <div className="feature-card-static max-w-3xl mx-auto p-8">
            <p className="text-text text-lg markdown-content">
              EatMeat is a vibrant app developed by OdinVistra LLC, dedicated to bringing people together through the love of meat. Our mission is to inspire home cooks and food enthusiasts with high-quality recipes, tips, and a community that celebrates bold flavors and culinary creativity. Learn more about OdinVistra LLC on our <Link to="/company-details">Company Details</Link> page.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-secondary-bg">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-primary text-center mb-12 animate-fade-in">
            Our Values
          </h2>
          <div className="feature-grid">
            <div className="feature-card-static">
              <h3 className="text-xl font-semibold text-primary mb-4">Community</h3>
              <p className="text-text">
                We foster a welcoming space where meat lovers can share recipes, stories, and tips.
              </p>
            </div>
            <div className="feature-card-static">
              <h3 className="text-xl font-semibold text-primary mb-4">Quality</h3>
              <p className="text-text">
                Our recipes and resources are crafted to ensure delicious, reliable results every time.
              </p>
            </div>
            <div className="feature-card-static">
              <h3 className="text-xl font-semibold text-primary mb-4">Creativity</h3>
              <p className="text-text">
                We encourage culinary exploration, from classic cuts to innovative dishes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6 animate-fade-in">
            Join the EatMeat Family
          </h2>
          <p className="text-text max-w-xl mx-auto mb-8">
            Whether you’re a seasoned chef or a home cook, EatMeat is here to fuel your passion for meat. Sign up today and start exploring!
          </p>
          <a
            href={isLoggedIn ? "/profile" : "/signup"}
            className="hero-button inline-block bg-primary text-background px-6 py-3 rounded-lg hover:bg-accent focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Get Started
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;